import { 
    DISALLOWED_CHARACTERS, 
    DISPLAY_NAME_FIELD, 
    DISPLAY_NAME_MAX_LENGTH, 
    FIELDS_TO_IGNORE_VALIDATIONS, 
    OTHER_FIELDS_MAX_LENGTH, 
    SPECIAL_CHARACTERS_MEASUREMENTS, 
    VARIATIONS_FIELD, 
    VARIATIONS_MAX_LENGTH 
} from "constants/measurements";

import { DuplicateValueInVariationsIssueSelectorError, DuplicateValueIssueSelectorError, DuplicateVariationsInValuesIssueSelectorError, DuplicateVariationsIssueSelectorError, InvalidValueIssueSelectorError, IssueSelectorError, IssueSelectorErrorType, MissingValueIssueSelectorError, SpecialCharactersInVariationsIssueSelectorError } from "data/Generic";
import { MeasurementRecord } from "data/UnitOfMeasurement";

/**
 * Helper: Checks if a value contains disallowed characters.
 */
const containsDisallowedCharacters = (value: string): boolean => {
    return DISALLOWED_CHARACTERS.test(value.toLowerCase()); // Convert to lowercase for case-insensitive check
};

/**
 * Helper: Gets the max length allowed for a field.
 */
const getMaxLength = (field: string): number => {
    switch (field) {
        case VARIATIONS_FIELD: return VARIATIONS_MAX_LENGTH;
        case DISPLAY_NAME_FIELD: return DISPLAY_NAME_MAX_LENGTH;
        default: return OTHER_FIELDS_MAX_LENGTH;
    }
};

/**
 * Helper: Extracts and normalizes variations (comma-separated values).
 */
const extractVariations = (value: string): string[] => {
    return value
        .split(',')
        .map(v => v.trim().toLowerCase())  // Normalize to lowercase for case-insensitive variations
        .filter(v => v.length > 0); // Remove empty values
};

/**
 * Converts the array of measurement records into a 2D array of `{ key, value }` pairs.
 */
const transformToKeyValueArray = (
    data: MeasurementRecord[]
): { id: number; errors?: string[], fields: { key: string; value: string }[] }[] => {
    return data.map(record => ({
        id: record.id,
        fields: Object.entries(record)
            .filter(([key]) => !FIELDS_TO_IGNORE_VALIDATIONS.includes(key)) // Exclude ignored fields
            .flatMap(([key, value]) => {
                if (key === VARIATIONS_FIELD && typeof value === "string") {
                    // Split variations into separate key-value pairs
                    return extractVariations(value).map(variation => ({
                        key: `${VARIATIONS_FIELD}`, // Keep the key name consistent
                        value: variation,
                    }));
                }
                return [{ key, value: value ? value.trim().toLowerCase() : "" }];
            }),
    }));
};

/**
 * Validates records by processing a 2D array of `{ key, value }` objects.
 */
const validateRecords = (data: any[]): IssueSelectorError[] => {
    let errors: IssueSelectorError[] = [];
    let seenValues = new Map<string, { record: any, key: string }>(); // Map to track duplicates across records

    for (let record of data) {
        record.errors = [];

        let seenVariations = new Set<string>();

        const variationsString = record.fields
            ?.filter((x: any) => x.key === VARIATIONS_FIELD && x.value?.trim())
            ?.map((x: any) => x.value)
            ?.join('')

        if(containsDisallowedCharacters(variationsString)){
            record.errors.push(VARIATIONS_FIELD);
            errors.push({
                type: IssueSelectorErrorType.SpecialCharactersNotAllowed,
                key: VARIATIONS_FIELD,
                data: []
            });
        }

        if(variationsString?.length > getMaxLength(VARIATIONS_FIELD)){
            record.errors.push(VARIATIONS_FIELD);
            errors.push({
                type: IssueSelectorErrorType.InvalidValue,
                key: VARIATIONS_FIELD,
                data: []
            });
        }

        for (let field of record.fields) {
            const { key, value } = field;

            // --- 1. Check for missing value ---
            if (!value) {
                if (!record.errors.includes(key)) {
                    record.errors.push(key);
                }

                errors.push({
                    type: IssueSelectorErrorType.MissingValue,
                    key,
                    data: [field]
                });
                continue;
            }

            const normalizedValue = value.toLowerCase();
            const existing = seenValues.get(normalizedValue);

            // --- 2. Check for duplicate variations within the same record ---
            if (key === VARIATIONS_FIELD) {
                if (seenVariations.has(value.toLowerCase())) {
                    if (!record.errors.includes(key)) {
                        record.errors.push(key);
                    }
                    errors.push({
                        type: IssueSelectorErrorType.DuplicateVariations,
                        key,
                        data: [field]
                    });
                } else {
                    seenVariations.add(value.toLowerCase());
                }
            } else {
                if(normalizedValue.length > getMaxLength(key)) {
                    record.errors.push(key);
                    errors.push({
                        type: IssueSelectorErrorType.InvalidValue,
                        key,
                        data: [field]
                    });
                }

                if(containsDisallowedCharacters(normalizedValue)){
                    record.errors.push(key);
                    errors.push({
                        type: IssueSelectorErrorType.SpecialCharactersNotAllowed,
                        key,
                        data: [field]
                    });
                }
            }

            // --- 3. Check for duplicate values across records ---

            if (existing && existing.record.id !== record.id) {
                // Avoid adding the same duplicate twice
                if (!record.errors.includes(key)) {
                    record.errors.push(key);
                }
                if (!existing.record.errors.includes(existing.key)) {
                    existing.record.errors.push(existing.key);
                }

                errors.push({
                    type: IssueSelectorErrorType.DuplicateValue,
                    key,
                    data: [field, { key: existing.key, value: normalizedValue }]
                });
            } else {
                seenValues.set(normalizedValue, { record, key });
            }
        }
    }

    return errors;
};

export const validateMeasurementsGrid = (data: MeasurementRecord[]): IssueSelectorError[] => {
    let mappedData = transformToKeyValueArray(data);
    let errors = validateRecords(mappedData);

    for(let record of mappedData){
        let dataRecord = data.find(x => x.id === record.id);
        if(dataRecord){
            dataRecord.errors = record.errors;
        }
    }

    return errors;
};

/**
 * Returns a user-friendly field name.
 */
export function fieldName(key: string): string {
    if (key.includes('-')) key = key.split('-')[0];

    switch (key) {
        case 'displayName': return 'Display name';
        case 'shortName': return 'Short name';
        case 'fullName': return 'Full name';
        case 'variations': return 'Variations';
        default: return key;
    }
}

/**
 * Generates error messages for display.
 */
export const getErrorText = (error: IssueSelectorError) => {
    switch (error.type) {
        case IssueSelectorErrorType.MissingValue:
            return {
                name: 'Missing value',
                description: `${fieldName((error as MissingValueIssueSelectorError).key)} cannot be blank.`,
                error
            };

        case IssueSelectorErrorType.DuplicateValue:
            let key_ = (error as DuplicateValueIssueSelectorError).key;
            return {
                name: 'Duplicate value',
                description: `${fieldName(key_)} ${error.data[0].value} is used in other measurement records.`,
                error
            };

        case IssueSelectorErrorType.InvalidValue:
            let key__ = (error as InvalidValueIssueSelectorError).key;
            return {
                name: 'Invalid value',
                description: `${fieldName(key__)} cannot be more than ${getMaxLength(key__)} characters`,
                error
            };

        case IssueSelectorErrorType.DuplicateVariations:
            console.log(error.data[0])
            return {
                name: 'Duplicate variations',
                description: `${fieldName((error as DuplicateVariationsIssueSelectorError).key)} ${error.data[0].value} is duplicated.`,
                error
            };

        case IssueSelectorErrorType.DuplicateValueInVariations:
            let key___ = (error as DuplicateValueInVariationsIssueSelectorError).key;
            return {
                name: 'Duplicate variation',
                description: `${fieldName(key___)} ${error.data[0].value} is duplicated in variation.`,
                error
            };

        case IssueSelectorErrorType.DuplicateVariationInValues:
            return {
                name: 'Duplicate variation',
                description: `${fieldName((error as DuplicateVariationsInValuesIssueSelectorError).key)} ${error.data[0].value} is duplicated in other measurement records.`,
                error
            };

        case IssueSelectorErrorType.SpecialCharactersNotAllowed:
            return {
                name: 'Special characters not allowed',
                description: `${fieldName((error as SpecialCharactersInVariationsIssueSelectorError).key)} cannot contain following special characters: ${SPECIAL_CHARACTERS_MEASUREMENTS}`,
                error
            };
        default:
            return { name: '', description: '', error };
    }
};
